import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrderService } from '../services/order-service';
import { TemporalWorkflowService } from '../services/temporal-workflow-service';
import httpClient from '../services/http-client';
import { 
  OrderSummaryDto,
  PendingApprovalDto,
  DivisionStatsDto,
  PagedResult,
  DashboardSummary
} from '../types';

const temporalWorkflowService = new TemporalWorkflowService(httpClient);

// Create an order service instance using the default HTTP client
const orderService = new OrderService(httpClient);

// Query keys
export const orderQueryKeys = {
  all: ['orders'] as const,
  lists: () => [...orderQueryKeys.all, 'list'] as const,
  list: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.lists(), divisionId, filters] as const,
  pendingApprovals: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.all, 'pending', divisionId, filters] as const,
  requiringApproval: (divisionId: string) => [...orderQueryKeys.all, 'requiring', divisionId] as const,
  submitted: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.all, 'submitted', divisionId, filters] as const,
  paused: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.all, 'paused', divisionId, filters] as const,
  completed: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.all, 'completed', divisionId, filters] as const,
  cancelled: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.all, 'cancelled', divisionId, filters] as const,
  needsResolved: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.all, 'needsResolved', divisionId, filters] as const,
  stats: (divisionId: string, filters: Record<string, any>) => [...orderQueryKeys.all, 'stats', divisionId, filters] as const,
  dashboard: (divisionId: string) => [...orderQueryKeys.all, 'dashboard', divisionId] as const,
  details: () => [...orderQueryKeys.all, 'detail'] as const,
  detail: (divisionId: string, orderId: string) => [...orderQueryKeys.details(), divisionId, orderId] as const,
};

/**
 * Hook to get all orders for a division
 */
export function useOrders(
  divisionId: string,
  params?: {
    page?: number;
    limit?: number;
    status?: string;
    submitterId?: string;
    startDate?: string;
    endDate?: string;
    updatedStartDate?: string;
    updatedEndDate?: string;
    minAmount?: number;
    maxAmount?: number;
    search?: string;
    step?: number;
    pendingOnly?: boolean;
    workflowId?: string;
    workflowName?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.list(divisionId, params || {}),
    queryFn: () => orderService.getOrders(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 30 * 1000, // 30 seconds for dynamic data
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * Hook to get pending approvals for a division
 */
export function usePendingApprovals(
  divisionId: string,
  params?: {
    userId?: string;
    page?: number;
    limit?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.pendingApprovals(divisionId, params || {}),
    queryFn: () => orderService.getPendingApprovals(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 15 * 1000, // 15 seconds for approval data
  });
}

/**
 * Hook to get orders requiring approval from the current user
 */
export function useOrdersRequiringApproval(divisionId: string, enabled = true) {
  return useQuery({
    queryKey: orderQueryKeys.requiringApproval(divisionId),
    queryFn: () => orderService.getOrdersRequiringApproval(divisionId),
    enabled: enabled && !!divisionId,
    staleTime: 15 * 1000, // 15 seconds for approval data
  });
}

export interface InApprovalFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  submitterId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  workflowId?: string;
}

/**
 * Hook to get all orders in approval process that user is involved with (with pagination, filtering, sorting)
 */
export function useOrdersInApproval(divisionId: string, filters: InApprovalFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['orders', 'in-approval', divisionId, filters],
    queryFn: () => orderService.getOrdersInApproval(divisionId, filters),
    enabled: enabled && !!divisionId,
    staleTime: 15 * 1000, // 15 seconds for approval data
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * Hook to get division statistics
 */
export function useDivisionStats(
  divisionId: string,
  params?: {
    startDate?: string;
    endDate?: string;
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.stats(divisionId, params || {}),
    queryFn: () => orderService.getDivisionStats(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 2 * 60 * 1000, // 2 minutes for stats
  });
}

/**
 * Hook to get dashboard summary data
 */
export function useDashboardSummary(divisionId: string, enabled = true) {
  return useQuery({
    queryKey: orderQueryKeys.dashboard(divisionId),
    queryFn: () => orderService.getDashboardSummary(divisionId),
    enabled: enabled && !!divisionId,
    staleTime: 30 * 1000, // 30 seconds for dashboard data
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  });
}

/**
 * Hook to get order detail
 */
export function useOrderDetail(divisionId: string, orderId: string, enabled = true) {
  return useQuery({
    queryKey: orderQueryKeys.detail(divisionId, orderId),
    queryFn: () => orderService.getOrderDetail(divisionId, orderId),
    enabled: enabled && !!divisionId && !!orderId,
    staleTime: 0, // Always fetch fresh data for order details
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchInterval: 30 * 1000, // Poll every 30 seconds for active orders
  });
}

/**
 * Hook to get submitted orders for a division
 */
export function useSubmittedOrders(
  divisionId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    submitterId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.submitted(divisionId, params || {}),
    queryFn: () => orderService.getSubmittedOrders(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 30 * 1000, // 30 seconds for submitted orders data
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * Hook to get paused orders for a division
 */
export function usePausedOrders(
  divisionId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    submitterId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.paused(divisionId, params || {}),
    queryFn: () => orderService.getPausedOrders(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 15 * 1000, // 15 seconds for paused orders data (refresh more often)
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * Hook to get completed orders for a division
 */
export function useCompletedOrders(
  divisionId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    submitterId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.completed(divisionId, params || {}),
    queryFn: () => orderService.getCompletedOrders(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 30 * 1000, // 30 seconds for completed orders data
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * Hook to get cancelled orders for a division
 */
export function useCancelledOrders(
  divisionId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    submitterId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.cancelled(divisionId, params || {}),
    queryFn: () => orderService.getCancelledOrders(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 30 * 1000, // 30 seconds for cancelled orders data
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * Hook to get orders that need resolution for a division
 */
export function useNeedsResolvedOrders(
  divisionId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  },
  enabled = true
) {
  return useQuery({
    queryKey: orderQueryKeys.needsResolved(divisionId, params || {}),
    queryFn: () => orderService.getNeedsResolvedOrders(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 30 * 1000, // 30 seconds for needs resolved orders data
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

/**
 * Combined hook for dashboard metrics - provides all dashboard data efficiently
 */
export function useDashboardMetrics(divisionId: string, enabled = true) {
  const ordersQuery = useOrders(divisionId, { limit: 1 }, enabled);
  const actionRequiredQuery = usePendingApprovals(divisionId, { limit: 1 }, enabled);
  const assignedToUserQuery = useOrdersRequiringApproval(divisionId, enabled);
  // Use the completed orders endpoint which includes both Completed and PartiallyCompleted statuses
  const completedOrdersQuery = useCompletedOrders(divisionId, { limit: 1 }, enabled);

  const isLoading = ordersQuery.isLoading || actionRequiredQuery.isLoading || 
                   assignedToUserQuery.isLoading || completedOrdersQuery.isLoading;
  
  const isError = ordersQuery.isError || actionRequiredQuery.isError || 
                 assignedToUserQuery.isError || completedOrdersQuery.isError;

  const error = ordersQuery.error || actionRequiredQuery.error || 
               assignedToUserQuery.error || completedOrdersQuery.error;

  const data: DashboardSummary | undefined = 
    (!isLoading && !isError && ordersQuery.data && actionRequiredQuery.data && 
     assignedToUserQuery.data && completedOrdersQuery.data) ? {
      totalOrders: ordersQuery.data.totalCount,
      actionRequiredOrders: actionRequiredQuery.data.totalCount,
      ordersAssignedToUser: assignedToUserQuery.data.length,
      completedOrders: completedOrdersQuery.data.totalCount,
    } : undefined;

  return {
    data,
    isLoading,
    isError,
    error,
    refetch: () => {
      ordersQuery.refetch();
      actionRequiredQuery.refetch();
      assignedToUserQuery.refetch();
      completedOrdersQuery.refetch();
    }
  };
}

/**
 * Mutation hook to reprocess approvers for an order
 */
export function useReprocessApprovers() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, orderId }: { divisionId: string; orderId: string }) =>
      orderService.reprocessApprovers(divisionId, orderId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
      queryClient.invalidateQueries({ 
        queryKey: orderQueryKeys.detail(variables.divisionId, variables.orderId) 
      });
    },
  });
}

/**
 * Mutation hook to restart workflow for an order (when workflow doesn't exist)
 */
export function useRestartWorkflow() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, orderId }: { divisionId: string; orderId: string }) =>
      orderService.restartWorkflow(divisionId, orderId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
      queryClient.invalidateQueries({ 
        queryKey: orderQueryKeys.detail(variables.divisionId, variables.orderId) 
      });
    },
  });
}

/**
 * Mutation hook to reprocess workflow matching for a submitted order without a workflow
 */
export function useReprocessOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, orderId }: { divisionId: string; orderId: string }) =>
      orderService.reprocessOrder(divisionId, orderId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
      queryClient.invalidateQueries({ 
        queryKey: orderQueryKeys.detail(variables.divisionId, variables.orderId) 
      });
    },
  });
}
/**
 * Mutation hook to pause a running workflow
 */
export function usePauseOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, orderId, reason }: { divisionId: string; orderId: string; reason?: string }) =>
      orderService.pauseOrder(divisionId, orderId, reason),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
      queryClient.invalidateQueries({ 
        queryKey: orderQueryKeys.detail(variables.divisionId, variables.orderId) 
      });
    },
  });
}

/**
 * Mutation hook to resume a paused workflow
 */
export function useResumeOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, orderId }: { divisionId: string; orderId: string }) =>
      orderService.resumeOrder(divisionId, orderId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
      queryClient.invalidateQueries({ 
        queryKey: orderQueryKeys.detail(variables.divisionId, variables.orderId) 
      });
    },
  });
}

/**
 * Admin-only mutation hook to permanently delete an order and close its Temporal workflow.
 * On success, invalidates all order list queries.
 */
export function useDeleteOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ divisionId, orderId }: { divisionId: string; orderId: string }) =>
      orderService.deleteOrder(divisionId, orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
    },
  });
}

/**
 * Mutation hook to fetch the live Temporal workflow status for an order (admin only)
 */
export function useRefreshTemporalStatus() {
  return useMutation({
    mutationFn: ({ divisionId, orderId }: { divisionId: string; orderId: string }) =>
      orderService.getTemporalStatus(divisionId, orderId),
  });
}

/**
 * Admin-only mutation hook to restart (re-run) the current approval level of a workflow.
 */
export function useAdminAdvanceWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ divisionId, temporalWorkflowId, reason }: {
      divisionId: string;
      temporalWorkflowId: string;
      reason: string;
    }) => temporalWorkflowService.adminRestartLevel(divisionId, temporalWorkflowId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
    },
  });
}

/**
 * Query hook to load rich Temporal workflow step detail (admin-only).
 * Fetches live step / approver data from the Temporal status endpoint.
 */
export function useTemporalWorkflowDetail(
  divisionId: string,
  temporalWorkflowId: string,
  enabled = false
) {
  return useQuery({
    queryKey: ['temporal-workflow-detail', divisionId, temporalWorkflowId],
    queryFn: () => temporalWorkflowService.getWorkflowDetail(divisionId, temporalWorkflowId),
    enabled: enabled && !!divisionId && !!temporalWorkflowId,
    staleTime: 30_000, // consider fresh for 30s
    retry: 1,
  });
}

/**
 * Utility hook to invalidate order cache data
 * Use this when you need to force refresh order data outside of mutations
 */
export function useInvalidateOrderCache() {
  const queryClient = useQueryClient();
  
  return {
    invalidateOrderDetail: (divisionId: string, orderId: string) => {
      return queryClient.invalidateQueries({ 
        queryKey: orderQueryKeys.detail(divisionId, orderId) 
      });
    },
    invalidateAllOrders: () => {
      return queryClient.invalidateQueries({ queryKey: orderQueryKeys.all });
    },
    refetchOrderDetail: (divisionId: string, orderId: string) => {
      return queryClient.refetchQueries({ 
        queryKey: orderQueryKeys.detail(divisionId, orderId) 
      });
    },
  };
}